import { useEffect, useState, useCallback } from "react";
import {
    RefreshCcw,
    AlertTriangle,
    Package,
    TrendingDown,
    Mail,
    ExternalLink,
    Plus,
    Beaker,
    ShieldAlert,
    FlaskConical,
    Wind,
    Flame,
    Droplet,
    Home as HomeIcon,
    X,
    Anchor,
    Clock,
    FileSignature,
    Check,
    Ban,
    Snowflake,
    Lock,
    LogOut,
} from "lucide-react";
import { toast } from "sonner";
import { api, getAdminToken, setAdminToken, clearAdminToken } from "@/lib/api";
import CommandersLog from "@/components/CommandersLog";
import EntityReinstatementTile from "@/components/EntityReinstatementTile";
import AdminAIStudio from "@/components/admin/AdminAIStudio";
import AdminFeaturesPanel from "@/components/admin/AdminFeaturesPanel";
import AdminPartnerAccessTile from "@/components/admin/AdminPartnerAccessTile";
import AdminOfferings from "@/components/admin/AdminOfferings";

function StatusPill({ status }) {
    const map = {
        ok: "bg-olive/20 text-forest border-olive/40",
        low: "bg-gold/20 text-brown border-gold/60",
        out: "bg-[#b14c3c]/20 text-[#7a2a1e] border-[#b14c3c]/60",
    };
    const label = { ok: "In Stock", low: "Low", out: "Out" }[status];
    return (
        <span
            className={`text-[0.6rem] tracking-[0.25em] uppercase px-2.5 py-1 rounded-full border ${map[status] || map.ok}`}
            data-testid={`status-pill-${status}`}
        >
            {label}
        </span>
    );
}

export default function Admin() {
    const [loading, setLoading] = useState(true);
    const [authed, setAuthed] = useState(() => !!getAdminToken());
    const [tokenInput, setTokenInput] = useState("");
    const [authError, setAuthError] = useState("");
    const [inventory, setInventory] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [replenishment, setReplenishment] = useState({ signals: [], count: 0 });
    const [batches, setBatches] = useState(1);
    const [restockAmt, setRestockAmt] = useState({});
    const [guardianOpen, setGuardianOpen] = useState(false);
    const [checks, setChecks] = useState({
        armor: false,
        atmosphere: false,
        emergency: false,
        goldenRule: false,
        perimeter: false,
    });
    const [confirming, setConfirming] = useState(false);

    // Governance · Dry-Dock Waiver
    const [vessel, setVessel] = useState(null);
    const [waivers, setWaivers] = useState([]);
    const [waiverReasons, setWaiverReasons] = useState([]);
    const [negativeClause, setNegativeClause] = useState("");
    const [waiverOpen, setWaiverOpen] = useState(false);
    const [waiverReason, setWaiverReason] = useState("");
    const [waiverNote, setWaiverNote] = useState("");
    const [waiverSubmitting, setWaiverSubmitting] = useState(false);

    // Growth Strike · Ad Boost
    const [boosts, setBoosts] = useState([]);
    const [boostMeta, setBoostMeta] = useState({
        platforms: ["tiktok", "meta", "youtube"],
        disclaimer: "",
    });
    const [boostOpen, setBoostOpen] = useState(false);
    const [boostAmount, setBoostAmount] = useState("");
    const [boostPlatform, setBoostPlatform] = useState("tiktok");
    const [boostReason, setBoostReason] = useState("");
    const [boostAck, setBoostAck] = useState(false);
    const [boostSubmitting, setBoostSubmitting] = useState(false);

    // Commander's Log slide-out
    const [logOpen, setLogOpen] = useState(false);

    // Reclamation Strike · Vessel status & ledger routing
    const [storeSettings, setStoreSettings] = useState(null);
    const [ledger, setLedger] = useState(null);
    const [routingDraft, setRoutingDraft] = useState({ cyberkids_pct: 10, director_pct: 70, pairing_pool_pct: 20 });
    const [reclamationBusy, setReclamationBusy] = useState(false);

    // Sovereign Pairing · Fulfillment queue
    const [fulfillment, setFulfillment] = useState({ items: [], counts: { pending: 0, submitted: 0, total: 0 }, printify_configured: false });

    const allChecked = Object.values(checks).every(Boolean);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [inv, sum, rep, ves, wv, wr, ab, abm, ss, lg, ff] = await Promise.all([
                api.get("/admin/inventory"),
                api.get("/admin/summary"),
                api.get("/admin/replenishment"),
                api.get("/admin/vessel-status"),
                api.get("/admin/waivers"),
                api.get("/admin/waivers/reasons"),
                api.get("/admin/ad-boosts"),
                api.get("/admin/ad-boosts/meta"),
                api.get("/admin/store/settings"),
                api.get("/admin/ledger/summary"),
                api.get("/admin/fulfillment/queue"),
            ]);
            setInventory(inv.data.items);
            setAlerts(inv.data.alerts);
            setSummary(sum.data);
            setReplenishment(rep.data);
            setVessel(ves.data);
            setWaivers(wv.data.waivers);
            setWaiverReasons(wr.data.reasons);
            setNegativeClause(wr.data.negative_clause);
            setBoosts(ab.data.applications);
            setBoostMeta(abm.data);
            setStoreSettings(ss.data);
            setLedger(lg.data);
            setFulfillment(ff.data);
            const lr = ss.data?.ledger_routing || { cyberkids_pct: 10, director_pct: 72, pairing_pool_pct: 18 };
            setRoutingDraft({
                cyberkids_pct: Number(lr.cyberkids_pct ?? 10),
                director_pct: Number(lr.director_pct ?? 72),
                pairing_pool_pct: Number(lr.pairing_pool_pct ?? 18),
            });
        } catch (e) {
            toast.error("Could not load admin data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!authed) { setLoading(false); return; }
        load();
    }, [load, authed]);

    const submitAdminToken = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const t = (tokenInput || "").trim();
        if (!t) { setAuthError("Enter the admin token"); return; }
        setAdminToken(t);
        try {
            // Verify token works by hitting a cheap admin endpoint
            await api.get("/admin/summary");
            setAuthed(true);
            setAuthError("");
            setTokenInput("");
            toast.success("Admin unlocked");
        } catch (err) {
            clearAdminToken();
            setAuthError("Invalid admin token");
        }
    };

    const lockAdmin = () => {
        clearAdminToken();
        setAuthed(false);
        toast.message("Admin locked");
    };

    if (!authed) {
        return (
            <div className="bg-cream min-h-screen flex items-center justify-center px-5" data-testid="admin-gate">
                <form onSubmit={submitAdminToken} className="w-full max-w-md p-8 bg-cream-warm rounded-sm border border-forest/15">
                    <div className="flex items-center gap-2 text-brown text-[0.65rem] tracking-[0.3em] uppercase">
                        <Lock size={12} /> Restricted · Director Only
                    </div>
                    <h1 className="font-serif text-3xl text-forest mt-3">Admin Access</h1>
                    <p className="text-sm text-forest/70 mt-2">
                        Enter the admin API token to unlock the dashboard. Token is stored locally on this device.
                    </p>
                    <label className="block mt-6 text-[0.65rem] tracking-[0.3em] uppercase text-brown">
                        Admin Token
                    </label>
                    <input
                        type="password"
                        value={tokenInput}
                        onChange={(e) => setTokenInput(e.target.value)}
                        className="mt-2 w-full px-4 py-3 rounded-sm border border-forest/20 bg-cream text-forest focus:outline-none focus:border-gold"
                        placeholder="paste bearer token"
                        autoFocus
                        data-testid="admin-token-input"
                    />
                    {authError && (
                        <p className="mt-2 text-sm text-[#b14c3c]" data-testid="admin-token-error">{authError}</p>
                    )}
                    <button
                        type="submit"
                        className="mt-5 w-full btn-pill btn-gold"
                        data-testid="admin-token-submit"
                    >
                        Unlock
                    </button>
                </form>
            </div>
        );
    }

    const onRunBatch = async () => {
        if (!allChecked) return;
        setConfirming(true);
        try {
            const { data } = await api.post("/admin/inventory/batch", {
                batches: Number(batches) || 1,
            });
            toast.success(`Deducted ${data.batches} × 50-bar batch`);
            setGuardianOpen(false);
            setChecks({
                armor: false,
                atmosphere: false,
                emergency: false,
                goldenRule: false,
                perimeter: false,
            });
            load();
        } catch {
            toast.error("Batch deduction failed");
        } finally {
            setConfirming(false);
        }
    };

    const openGuardian = () => {
        setChecks({
            armor: false,
            atmosphere: false,
            emergency: false,
            goldenRule: false,
            perimeter: false,
        });
        setGuardianOpen(true);
    };

    // ---- Dry-Dock Waiver handlers ----
    const openWaiver = () => {
        setWaiverReason("");
        setWaiverNote("");
        setWaiverOpen(true);
    };
    const onSubmitWaiver = async () => {
        if (!waiverReason) {
            toast.error("Pick a sanctioned reason");
            return;
        }
        setWaiverSubmitting(true);
        try {
            await api.post("/admin/waivers", {
                reason_key: waiverReason,
                note: waiverNote,
            });
            toast.success(
                "Request Pending Director Signature — intel queued for Director K.",
            );
            setWaiverOpen(false);
            load();
        } catch (e) {
            const msg =
                e?.response?.data?.detail || "Submission failed";
            toast.error(msg);
        } finally {
            setWaiverSubmitting(false);
        }
    };
    const onWaiverDecide = async (id, decision) => {
        try {
            await api.post(`/admin/waivers/${id}/decision`, { decision });
            toast.success(
                decision === "approved"
                    ? "Waiver approved · clock frozen"
                    : "Waiver denied · clock continues",
            );
            load();
        } catch {
            toast.error("Decision failed");
        }
    };

    // ---- Ad Boost handlers ----
    const openBoost = () => {
        setBoostAmount("");
        setBoostPlatform("tiktok");
        setBoostReason("");
        setBoostAck(false);
        setBoostOpen(true);
    };
    const onSubmitBoost = async () => {
        const amt = parseFloat(boostAmount);
        if (!amt || amt < 50) {
            toast.error("Boost amount must be at least $50");
            return;
        }
        if (!boostReason.trim()) {
            toast.error("Strike reason required");
            return;
        }
        if (!boostAck) {
            toast.error("Acknowledge the Capital Injection clause to proceed");
            return;
        }
        setBoostSubmitting(true);
        try {
            await api.post("/admin/ad-boosts", {
                amount: amt,
                platform: boostPlatform,
                strike_reason: boostReason.trim(),
                acknowledged: boostAck,
            });
            toast.success("Ad Boost Intel Brief filed for Director K.");
            setBoostOpen(false);
            load();
        } catch (e) {
            const msg =
                e?.response?.data?.detail || "Application failed";
            toast.error(msg);
        } finally {
            setBoostSubmitting(false);
        }
    };
    const onBoostDecide = async (id, decision) => {
        try {
            await api.post(`/admin/ad-boosts/${id}/decision`, { decision });
            toast.success(`Application marked ${decision}`);
            load();
        } catch {
            toast.error("Decision failed");
        }
    };

    const onRestock = async (key) => {
        const amount = Number(restockAmt[key]);
        if (!amount || amount <= 0) {
            toast.error("Enter a positive amount");
            return;
        }
        try {
            await api.post("/admin/inventory/restock", { key, amount_oz: amount });
            toast.success(`Restocked ${amount} oz`);
            setRestockAmt((s) => ({ ...s, [key]: "" }));
            load();
        } catch {
            toast.error("Restock failed");
        }
    };

    // ---- Reclamation Strike handlers ----
    const onSetFleetMode = async (mode) => {
        setReclamationBusy(true);
        try {
            const { data } = await api.post("/admin/store/settings", { fleet_mode: mode });
            setStoreSettings(data);
            const labels = { curing: "Curing", live: "Live", ghost_protocol: "Ghost Protocol" };
            toast.success(`Vessel status → ${labels[mode] || mode}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Status flip failed");
        } finally {
            setReclamationBusy(false);
        }
    };

    const onUpdateRouting = async () => {
        const cyber = Number(routingDraft.cyberkids_pct);
        const director = Number(routingDraft.director_pct);
        const pairing = Number(routingDraft.pairing_pool_pct);
        if (cyber + director + pairing !== 100) {
            toast.error("Routing must sum to 100%");
            return;
        }
        setReclamationBusy(true);
        try {
            const { data } = await api.post("/admin/store/settings", {
                ledger_routing: {
                    cyberkids_pct: cyber,
                    director_pct: director,
                    pairing_pool_pct: pairing,
                },
            });
            setStoreSettings(data);
            toast.success(
                `Ledger routed · ${director}% Director / ${pairing}% Pool / ${cyber}% CyberKids`,
            );
            const lg = await api.get("/admin/ledger/summary");
            setLedger(lg.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Routing update failed");
        } finally {
            setReclamationBusy(false);
        }
    };

    return (
        <div className="bg-cream min-h-screen" data-testid="admin-page">
            <section className="max-w-7xl mx-auto px-5 md:px-10 pt-16 pb-24">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <span className="divider-rule text-brown">
                            Ghost Crew · Studio Ops
                        </span>
                        <h1 className="font-serif text-5xl sm:text-6xl text-forest mt-5">
                            Admin <span className="italic text-gold">Dashboard</span>
                        </h1>
                    </div>
                    <button
                        onClick={load}
                        className="btn-pill btn-ghost"
                        data-testid="admin-refresh-btn"
                    >
                        <RefreshCcw size={14} /> Refresh
                    </button>
                    <button
                        onClick={() => setLogOpen(true)}
                        className="btn-pill btn-gold ml-2"
                        data-testid="commanders-log-open-btn"
                    >
                        <FileSignature size={14} /> Commander's Log
                    </button>
                    <button
                        onClick={lockAdmin}
                        className="btn-pill btn-ghost ml-2"
                        data-testid="admin-lock-btn"
                        title="Lock admin and clear token"
                    >
                        <LogOut size={14} /> Lock
                    </button>
                </div>

                {/* Summary cards */}
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        {
                            label: "Paid Orders",
                            value: summary?.orders ?? "—",
                            icon: Package,
                        },
                        {
                            label: "Paid Transactions",
                            value: summary?.paid_transactions ?? "—",
                            icon: TrendingDown,
                        },
                        {
                            label: "Newsletter",
                            value: summary?.newsletter_subscribers ?? "—",
                            icon: Mail,
                        },
                        {
                            label: "Low-Stock Alerts",
                            value: summary?.low_stock_alerts?.length ?? 0,
                            icon: AlertTriangle,
                        },
                    ].map((c) => {
                        const Icon = c.icon;
                        return (
                            <div
                                key={c.label}
                                className="p-5 bg-cream-warm rounded-sm border border-forest/10"
                                data-testid={`summary-card-${c.label.toLowerCase().replace(/ /g, "-")}`}
                            >
                                <div className="flex items-center gap-2 text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                    <Icon size={12} /> {c.label}
                                </div>
                                <p className="font-serif text-3xl text-forest mt-2">
                                    {c.value}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* EIGOO Inc. · DCCA Reinstatement progress + reserve ledger */}
                <EntityReinstatementTile />

                {/* Partner Access · Owner self-serve (allowlist, invites, owners) */}
                <AdminPartnerAccessTile />
                <AdminOfferings />

                <div className="mt-8">
                    <AdminAIStudio />
                </div>

                <AdminFeaturesPanel />

                {/* Reclamation Strike · Vessel status & ledger */}
                <div
                    className="mt-10 grid lg:grid-cols-12 gap-6"
                    data-testid="reclamation-strike-card"
                >
                    <div className="lg:col-span-7 p-6 bg-cream-warm rounded-sm border border-forest/10">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div>
                                <span className="divider-rule text-brown">
                                    <Anchor size={12} /> Reclamation Strike · Vessel Status
                                </span>
                                <h2 className="font-serif text-3xl text-forest mt-3">
                                    {(() => {
                                        const m = storeSettings?.fleet_mode || "curing";
                                        return m === "ghost_protocol"
                                            ? "Ghost Protocol"
                                            : m === "live"
                                              ? "Live"
                                              : "Curing";
                                    })()}
                                </h2>
                            </div>
                            <span
                                className={`text-[0.6rem] tracking-[0.3em] uppercase px-3 py-1.5 rounded-full border ${
                                    storeSettings?.fleet_mode === "ghost_protocol"
                                        ? "bg-forest text-cream border-forest"
                                        : storeSettings?.fleet_mode === "live"
                                          ? "bg-gold/30 text-brown border-gold/60"
                                          : "bg-olive/20 text-forest border-olive/40"
                                }`}
                                data-testid="vessel-status-pill"
                            >
                                {storeSettings?.fleet_mode || "curing"}
                            </span>
                        </div>
                        <p className="text-sm text-brown mt-3 leading-relaxed">
                            Switch storefront posture. <strong>Ghost Protocol</strong> redacts
                            personal branding, leads with Sovereign Provisions, and routes
                            profit signals via the Director's Ledger split.
                        </p>
                        <div className="mt-5 grid grid-cols-3 gap-3">
                            {[
                                { key: "curing", label: "Curing", testid: "fleet-curing-btn" },
                                { key: "live", label: "Live", testid: "fleet-live-btn" },
                                { key: "ghost_protocol", label: "Ghost", testid: "fleet-ghost-btn" },
                            ].map((opt) => {
                                const active = storeSettings?.fleet_mode === opt.key;
                                return (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        disabled={reclamationBusy || active}
                                        onClick={() => onSetFleetMode(opt.key)}
                                        className={`btn-pill ${active ? "btn-gold" : "btn-ghost"} disabled:opacity-60`}
                                        data-testid={opt.testid}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="lg:col-span-5 p-6 bg-forest text-cream rounded-sm">
                        <span className="divider-rule text-gold">
                            Director's Ledger
                        </span>
                        <h3 className="font-serif text-2xl mt-3">
                            ${ledger?.director_share?.toFixed(2) ?? "0.00"}
                        </h3>
                        <div className="mt-2 text-[0.7rem] tracking-[0.25em] uppercase text-cream/70">
                            Pairing Pool · ${ledger?.pairing_pool_share?.toFixed(2) ?? "0.00"}
                        </div>
                        <div className="mt-1 text-[0.7rem] tracking-[0.25em] uppercase text-cream/70">
                            CyberKids · ${ledger?.cyberkids_share?.toFixed(2) ?? "0.00"}
                        </div>
                        <div className="mt-2 text-[0.7rem] tracking-[0.25em] uppercase text-cream/60">
                            Gross paid · ${ledger?.gross_paid?.toFixed(2) ?? "0.00"} ·{" "}
                            {ledger?.order_count ?? 0} orders ·{" "}
                            {ledger?.bundle_count ?? 0} bundles
                        </div>

                        <div className="mt-5 grid grid-cols-3 gap-2">
                            <label className="block">
                                <span className="text-[0.55rem] tracking-[0.3em] uppercase text-cream/70">
                                    Cyber %
                                </span>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={routingDraft.cyberkids_pct}
                                    onChange={(e) => {
                                        const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                                        setRoutingDraft((r) => {
                                            const remaining = Math.max(0, 100 - v);
                                            // preserve director:pairing ratio from current draft
                                            const oldSum = (r.director_pct || 0) + (r.pairing_pool_pct || 0);
                                            const dirShare = oldSum > 0 ? r.director_pct / oldSum : 0.8;
                                            const newDir = Math.round(remaining * dirShare);
                                            return {
                                                cyberkids_pct: v,
                                                director_pct: newDir,
                                                pairing_pool_pct: remaining - newDir,
                                            };
                                        });
                                    }}
                                    className="mt-1 w-full px-2 py-2 rounded-sm bg-cream/10 text-cream border border-cream/20 focus:outline-none focus:border-gold text-sm"
                                    data-testid="routing-cyberkids-input"
                                />
                            </label>
                            <label className="block">
                                <span className="text-[0.55rem] tracking-[0.3em] uppercase text-cream/70">
                                    Director %
                                </span>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={routingDraft.director_pct}
                                    onChange={(e) => {
                                        const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                                        setRoutingDraft((r) => ({
                                            ...r,
                                            director_pct: v,
                                            pairing_pool_pct: Math.max(0, 100 - r.cyberkids_pct - v),
                                        }));
                                    }}
                                    className="mt-1 w-full px-2 py-2 rounded-sm bg-cream/10 text-cream border border-cream/20 focus:outline-none focus:border-gold text-sm"
                                    data-testid="routing-director-input"
                                />
                            </label>
                            <label className="block">
                                <span className="text-[0.55rem] tracking-[0.3em] uppercase text-cream/70">
                                    Pool %
                                </span>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={routingDraft.pairing_pool_pct}
                                    onChange={(e) => {
                                        const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                                        setRoutingDraft((r) => ({
                                            ...r,
                                            pairing_pool_pct: v,
                                            director_pct: Math.max(0, 100 - r.cyberkids_pct - v),
                                        }));
                                    }}
                                    className="mt-1 w-full px-2 py-2 rounded-sm bg-cream/10 text-cream border border-cream/20 focus:outline-none focus:border-gold text-sm"
                                    data-testid="routing-pairing-input"
                                />
                            </label>
                        </div>
                        <button
                            type="button"
                            onClick={onUpdateRouting}
                            disabled={reclamationBusy}
                            className="btn-pill btn-gold mt-4 w-full disabled:opacity-60"
                            data-testid="routing-save-btn"
                        >
                            Route Profit Signals
                        </button>
                    </div>
                </div>

                {/* Sovereign Bundle Bridge · Printify Fulfillment Queue */}
                <div
                    className="mt-6 p-6 bg-cream-warm rounded-sm border border-forest/10"
                    data-testid="fulfillment-bridge-card"
                >
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <span className="divider-rule text-brown">
                                <Package size={12} /> Sovereign Bundle · Printify Bridge
                            </span>
                            <h3 className="font-serif text-2xl text-forest mt-2">
                                Fulfillment Queue ·{" "}
                                <span className="text-gold">
                                    {fulfillment?.counts?.total ?? 0}
                                </span>
                            </h3>
                            <p className="text-[0.65rem] tracking-[0.25em] uppercase text-brown/70 mt-1">
                                {fulfillment?.printify_configured
                                    ? "API key · ACTIVE"
                                    : "API key · NOT SET (queueing as MOCKED until PRINTIFY_API_KEY env var is wired)"}
                            </p>
                        </div>
                        <div className="flex gap-3 text-[0.6rem] tracking-[0.3em] uppercase">
                            <span className="px-3 py-1.5 rounded-full bg-gold/20 text-brown border border-gold/40">
                                Pending · {fulfillment?.counts?.pending ?? 0}
                            </span>
                            <span className="px-3 py-1.5 rounded-full bg-olive/20 text-forest border border-olive/40">
                                Submitted · {fulfillment?.counts?.submitted ?? 0}
                            </span>
                        </div>
                    </div>

                    {fulfillment?.items?.length > 0 ? (
                        <div className="mt-5 space-y-2 max-h-64 overflow-auto">
                            {fulfillment.items.slice(0, 20).map((row) => (
                                <div
                                    key={row.id}
                                    className="flex items-center justify-between gap-3 p-3 bg-cream rounded-sm border border-forest/10 text-sm"
                                    data-testid={`fulfillment-row-${row.id}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="font-serif text-forest truncate">
                                            {row.line_items
                                                ?.map((l) => `${l.name} ×${l.quantity}`)
                                                .join(" · ")}
                                        </div>
                                        <div className="text-[0.6rem] tracking-[0.25em] uppercase text-brown/70 mt-0.5">
                                            Order {(row.order_id || "").slice(0, 8)} ·{" "}
                                            {row.customer_email || "guest"}
                                        </div>
                                    </div>
                                    <span
                                        className={`text-[0.55rem] tracking-[0.3em] uppercase px-2 py-1 rounded-full border ${
                                            row.status === "submitted"
                                                ? "bg-olive/20 text-forest border-olive/40"
                                                : "bg-gold/20 text-brown border-gold/60"
                                        }`}
                                    >
                                        {row.status === "submitted" ? "submitted" : "pending"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="mt-5 text-sm text-brown/70 italic">
                            No lifestyle orders bridged yet — the queue activates the
                            moment a Modern Director Bundle (or Anchor Tee / Joggers /
                            Sneakers) is paid.
                        </p>
                    )}
                </div>

                {/* Alerts (soap-only — hidden under Ghost Protocol POD mode) */}
                {storeSettings && storeSettings.fleet_mode !== "ghost_protocol" && alerts.length > 0 && (
                    <div
                        className="mt-8 p-5 bg-[#fdf3e2] border border-gold/60 rounded-sm"
                        data-testid="admin-alerts"
                    >
                        <div className="flex items-center gap-2 text-gold font-semibold tracking-[0.2em] uppercase text-[0.7rem]">
                            <AlertTriangle size={14} /> Low Inventory Signal
                        </div>
                        <p className="text-forest text-sm mt-2 leading-relaxed">
                            {alerts.length} ingredient{alerts.length > 1 ? "s" : ""} below 3× batch threshold. Restock below to keep the pour line flowing.
                        </p>
                    </div>
                )}

                {/* Batch deduction + Replenishment (soap-only — hidden under Ghost Protocol) */}
                {storeSettings && storeSettings.fleet_mode !== "ghost_protocol" && (
                <div
                    className="mt-10 grid md:grid-cols-12 gap-6"
                    data-testid="admin-batch-card"
                >
                    <div className="md:col-span-5 p-6 bg-forest text-cream rounded-sm relative overflow-hidden">
                        <div className="grain absolute inset-0" />
                        <div className="relative">
                            <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.3em] uppercase text-gold">
                                <Beaker size={14} /> Run a Pour
                            </div>
                            <h2 className="font-serif text-3xl mt-3">
                                Log a 50-bar batch
                            </h2>
                            <p className="text-cream/70 text-sm mt-3 leading-relaxed">
                                Deducts 80 / 60 / 40 / 20 / 29 oz of Coconut, Olive, Shea, Castor, and Lye per batch.
                            </p>
                            <div className="mt-6 flex items-center gap-3">
                                <input
                                    type="number"
                                    min={1}
                                    max={20}
                                    value={batches}
                                    onChange={(e) => setBatches(e.target.value)}
                                    className="w-20 bg-transparent border-b border-cream/30 text-cream font-serif text-xl py-1 outline-none focus:border-gold"
                                    data-testid="admin-batch-input"
                                />
                                <span className="text-[0.65rem] tracking-[0.3em] uppercase text-cream/60">
                                    × 50-bar batches
                                </span>
                            </div>
                            <button
                                onClick={openGuardian}
                                className="btn-pill btn-gold mt-6"
                                data-testid="admin-create-batch-btn"
                            >
                                <ShieldAlert size={14} /> Create New Batch
                            </button>
                        </div>
                    </div>
                    <div className="md:col-span-7 p-6 bg-cream-warm rounded-sm border border-forest/10">
                        <div className="text-[0.65rem] tracking-[0.3em] uppercase text-brown">
                            Replenishment Signals · T+21
                        </div>
                        <h2 className="font-serif text-3xl text-forest mt-3">
                            {replenishment.count} customer
                            {replenishment.count === 1 ? "" : "s"} due for a bar.
                        </h2>
                        {replenishment.signals.length === 0 ? (
                            <p className="text-brown text-sm mt-4">
                                No eligible customers yet. Once orders pass 21 days, they'll queue here for a restock nudge.
                            </p>
                        ) : (
                            <ul className="mt-5 divide-y divide-forest/10">
                                {replenishment.signals.slice(0, 6).map((s) => (
                                    <li
                                        key={s.email}
                                        className="py-3 flex items-center justify-between gap-3 text-sm"
                                        data-testid={`replenishment-${s.email}`}
                                    >
                                        <span className="text-forest">
                                            {s.email}
                                        </span>
                                        <span className="text-brown text-xs">
                                            Last order{" "}
                                            {new Date(
                                                s.last_order_at,
                                            ).toLocaleDateString()}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
                )}

                {/* === GOVERNANCE · Vessel Status & Dry-Dock Waiver === */}
                <div
                    className="mt-12 grid md:grid-cols-12 gap-6"
                    data-testid="vessel-status-section"
                >
                    <div className="md:col-span-7 p-6 bg-cream-warm rounded-sm border border-forest/10 relative overflow-hidden">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.3em] uppercase text-brown">
                                    <Anchor size={12} /> Vessel Status
                                </div>
                                <h2 className="font-serif text-3xl text-forest mt-2">
                                    Stalled Engine Clock
                                </h2>
                            </div>
                            <button
                                onClick={openWaiver}
                                className="btn-pill btn-primary"
                                data-testid="request-waiver-btn"
                            >
                                <FileSignature size={14} /> Request Dry-Dock Waiver
                            </button>
                        </div>
                        <div className="mt-6 grid grid-cols-3 gap-4">
                            <div>
                                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                    Days Remaining
                                </p>
                                <p
                                    className="font-serif text-4xl text-forest mt-1"
                                    data-testid="vessel-days-remaining"
                                >
                                    {vessel?.days_remaining?.toFixed(1) ?? "—"}
                                    <span className="text-sm text-brown ml-1">
                                        / {vessel?.stalled_engine_days ?? 30}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                    Status
                                </p>
                                <p
                                    className={`font-serif text-2xl mt-1 ${vessel?.clock_frozen ? "text-gold" : "text-forest"}`}
                                    data-testid="vessel-clock-status"
                                >
                                    {vessel?.clock_frozen ? (
                                        <span className="inline-flex items-center gap-2">
                                            <Snowflake size={20} /> Frozen
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-2">
                                            <Clock size={20} /> Counting
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div>
                                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                    Pending Waivers
                                </p>
                                <p
                                    className="font-serif text-4xl text-forest mt-1"
                                    data-testid="vessel-pending-waivers"
                                >
                                    {vessel?.pending_waivers ?? 0}
                                </p>
                            </div>
                        </div>
                        {vessel?.active_waiver && (
                            <p className="mt-4 text-xs text-brown">
                                Frozen by approved waiver:{" "}
                                <span className="font-serif italic text-forest">
                                    {vessel.active_waiver.reason_label}
                                </span>
                            </p>
                        )}
                    </div>

                    <div
                        className="md:col-span-5 p-6 bg-forest text-cream rounded-sm relative overflow-hidden"
                        data-testid="waivers-list"
                    >
                        <div className="grain absolute inset-0 opacity-50" />
                        <div className="relative">
                            <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.3em] uppercase text-gold">
                                <FileSignature size={12} /> Director's Desk
                            </div>
                            <h3 className="font-serif text-2xl mt-2">
                                Recent Waivers
                            </h3>
                            {waivers.length === 0 ? (
                                <p className="text-cream/60 text-sm mt-4">
                                    No waivers filed. The fleet sails on.
                                </p>
                            ) : (
                                <ul className="mt-4 space-y-3 max-h-72 overflow-y-auto pr-1">
                                    {waivers.slice(0, 6).map((w) => (
                                        <li
                                            key={w.id}
                                            className="p-3 rounded-sm border border-cream/15 bg-forest-deep/50"
                                            data-testid={`waiver-row-${w.id}`}
                                        >
                                            <div className="flex items-baseline justify-between gap-2">
                                                <p className="font-serif text-base">
                                                    {w.reason_label}
                                                </p>
                                                <span
                                                    className={`text-[0.55rem] tracking-[0.25em] uppercase ${
                                                        w.status === "approved"
                                                            ? "text-gold"
                                                            : w.status === "denied"
                                                              ? "text-[#e07a5f]"
                                                              : "text-cream/70"
                                                    }`}
                                                >
                                                    {w.status}
                                                </span>
                                            </div>
                                            <p className="text-[0.65rem] text-cream/55 mt-0.5">
                                                {w.reason_subtitle}
                                            </p>
                                            {w.note && (
                                                <p className="text-xs text-cream/70 mt-1.5 italic">
                                                    "{w.note}"
                                                </p>
                                            )}
                                            {w.status === "pending" && (
                                                <div className="mt-2 flex gap-2">
                                                    <button
                                                        onClick={() =>
                                                            onWaiverDecide(
                                                                w.id,
                                                                "approved",
                                                            )
                                                        }
                                                        className="text-[0.6rem] tracking-[0.25em] uppercase text-gold hover:text-cream inline-flex items-center gap-1"
                                                        data-testid={`waiver-approve-${w.id}`}
                                                    >
                                                        <Check size={12} /> Approve
                                                    </button>
                                                    <span className="text-cream/30">
                                                        ·
                                                    </span>
                                                    <button
                                                        onClick={() =>
                                                            onWaiverDecide(
                                                                w.id,
                                                                "denied",
                                                            )
                                                        }
                                                        className="text-[0.6rem] tracking-[0.25em] uppercase text-cream/70 hover:text-[#e07a5f] inline-flex items-center gap-1"
                                                        data-testid={`waiver-deny-${w.id}`}
                                                    >
                                                        <Ban size={12} /> Deny
                                                    </button>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>

                {/* === GROWTH STRIKE · Ad Boost === */}
                <div
                    className="mt-10 p-6 md:p-8 rounded-sm relative overflow-hidden"
                    style={{
                        background:
                            "linear-gradient(135deg, var(--ig-gold) 0%, #e6ba75 60%, var(--ig-cream-warm) 100%)",
                    }}
                    data-testid="ad-boost-section"
                >
                    <div className="grain absolute inset-0 opacity-30" />
                    <div className="relative grid md:grid-cols-12 gap-6 items-center">
                        <div className="md:col-span-7">
                            <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.3em] uppercase text-forest">
                                <Flame size={12} /> Growth Strike · Capital Injection
                            </div>
                            <h2 className="font-serif text-3xl md:text-4xl text-forest mt-3 leading-tight">
                                Apply for an{" "}
                                <span className="italic">Advertisement Boost.</span>
                            </h2>
                            <p className="mt-3 text-forest/80 text-sm md:text-base leading-relaxed max-w-xl">
                                Fuel for the fleet — a capital injection from
                                EIGOO Inc. for paid acquisition on TikTok, Meta,
                                or YouTube. Subject to a profit-percentage
                                renegotiation reflecting the Fleet's capital risk.
                            </p>
                        </div>
                        <div className="md:col-span-5 flex md:justify-end">
                            <button
                                onClick={openBoost}
                                className="btn-pill bg-forest text-cream border border-forest hover:bg-forest-deep"
                                data-testid="apply-ad-boost-btn"
                            >
                                <Flame size={14} /> Apply for Ad Boost
                            </button>
                        </div>
                    </div>
                </div>
                {/* Past applications */}
                {boosts.length > 0 && (
                    <div
                        className="mt-6 p-6 bg-cream-warm rounded-sm border border-forest/10"
                        data-testid="ad-boost-list"
                    >
                        <h3 className="font-serif text-2xl text-forest">
                            Filed Applications
                        </h3>
                        <ul className="mt-4 divide-y divide-forest/10">
                            {boosts.slice(0, 6).map((b) => (
                                <li
                                    key={b.id}
                                    className="py-4 grid md:grid-cols-12 gap-3 items-center"
                                    data-testid={`ad-boost-row-${b.id}`}
                                >
                                    <div className="md:col-span-2">
                                        <p className="font-serif text-2xl text-forest">
                                            ${b.amount.toFixed(0)}
                                        </p>
                                        <p className="text-[0.6rem] tracking-[0.25em] uppercase text-brown mt-0.5">
                                            {b.platform}
                                        </p>
                                    </div>
                                    <div className="md:col-span-7 text-sm text-forest">
                                        <p className="font-serif italic line-clamp-2">
                                            "{b.strike_reason}"
                                        </p>
                                        <p className="text-[0.6rem] text-brown mt-1">
                                            {new Date(b.created_at).toLocaleString()}
                                        </p>
                                    </div>
                                    <div className="md:col-span-3 flex items-center justify-end gap-2">
                                        <span
                                            className={`text-[0.6rem] tracking-[0.25em] uppercase px-2.5 py-1 rounded-full border ${
                                                b.status === "approved"
                                                    ? "bg-olive/20 border-olive/50 text-forest"
                                                    : b.status === "denied"
                                                      ? "bg-[#e07a5f]/20 border-[#e07a5f]/50 text-[#7a2a1e]"
                                                      : b.status === "reviewed"
                                                        ? "bg-cream border-forest/30 text-forest"
                                                        : "bg-gold/20 border-gold/60 text-brown"
                                            }`}
                                        >
                                            {b.status}
                                        </span>
                                        {b.status === "pending" && (
                                            <>
                                                <button
                                                    onClick={() =>
                                                        onBoostDecide(
                                                            b.id,
                                                            "approved",
                                                        )
                                                    }
                                                    className="text-[0.6rem] tracking-[0.25em] uppercase text-forest hover:text-gold"
                                                    data-testid={`ad-boost-approve-${b.id}`}
                                                >
                                                    <Check size={12} />
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        onBoostDecide(
                                                            b.id,
                                                            "denied",
                                                        )
                                                    }
                                                    className="text-[0.6rem] tracking-[0.25em] uppercase text-brown hover:text-[#7a2a1e]"
                                                    data-testid={`ad-boost-deny-${b.id}`}
                                                >
                                                    <Ban size={12} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Inventory table (soap-only — hidden under Ghost Protocol POD mode) */}
                {storeSettings && storeSettings.fleet_mode !== "ghost_protocol" && (
                <div className="mt-10" data-testid="admin-inventory">
                    <h2 className="font-serif text-3xl text-forest">
                        Raw Ingredient Stock
                    </h2>
                    <div className="mt-6 grid gap-4">
                        {inventory.map((row) => (
                            <article
                                key={row.key}
                                className="p-5 bg-cream-warm rounded-sm border border-forest/10 grid md:grid-cols-12 gap-4 items-center"
                                data-testid={`inventory-row-${row.key}`}
                            >
                                <div className="md:col-span-3">
                                    <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                        {row.role}
                                    </p>
                                    <h3 className="font-serif text-2xl text-forest mt-1">
                                        {row.label}
                                    </h3>
                                </div>
                                <div className="md:col-span-3 text-sm">
                                    <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                        On Hand
                                    </p>
                                    <p className="font-serif text-2xl text-forest mt-1">
                                        {row.stock_oz.toFixed(1)}{" "}
                                        <span className="text-sm text-brown">
                                            oz
                                        </span>
                                    </p>
                                    <p className="text-xs text-brown mt-1">
                                        {row.batches_remaining.toFixed(1)} ×
                                        batches · threshold{" "}
                                        {row.low_stock_threshold} oz
                                    </p>
                                    <div className="mt-2">
                                        <StatusPill status={row.status} />
                                    </div>
                                </div>
                                <div className="md:col-span-3 flex items-center gap-2">
                                    <input
                                        type="number"
                                        min={0}
                                        placeholder="oz"
                                        value={restockAmt[row.key] || ""}
                                        onChange={(e) =>
                                            setRestockAmt((s) => ({
                                                ...s,
                                                [row.key]: e.target.value,
                                            }))
                                        }
                                        className="w-24 bg-cream border-b border-forest/30 focus:border-gold py-1.5 outline-none text-forest"
                                        data-testid={`restock-input-${row.key}`}
                                    />
                                    <button
                                        onClick={() => onRestock(row.key)}
                                        className="btn-pill btn-ghost px-4 py-2 text-[0.65rem]"
                                        data-testid={`restock-btn-${row.key}`}
                                    >
                                        <Plus size={12} /> Add
                                    </button>
                                </div>
                                <div className="md:col-span-3 flex flex-col gap-2 items-end">
                                    {row.restock_links?.map((l) => (
                                        <a
                                            key={l.url}
                                            href={l.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-forest hover:text-gold inline-flex items-center gap-1 tracking-wide"
                                            data-testid={`restock-link-${row.key}-${l.vendor.replace(/\s/g, "-").toLowerCase()}`}
                                        >
                                            {l.vendor}
                                            <ExternalLink size={12} />
                                        </a>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
                )}
            </section>

            {/* Guardian Protocol Modal */}
            {guardianOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-forest-deep/85 backdrop-blur-sm"
                    role="dialog"
                    aria-modal="true"
                    data-testid="guardian-modal"
                >
                    <div className="relative max-w-lg w-full bg-cream rounded-sm shadow-2xl border border-gold/40 overflow-hidden">
                        <div className="grain absolute inset-0 opacity-30 pointer-events-none" />
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setGuardianOpen(false)}
                                className="absolute top-4 right-4 text-brown hover:text-forest transition-colors"
                                aria-label="Close"
                                data-testid="guardian-close-btn"
                            >
                                <X size={18} />
                            </button>
                            <div className="px-7 pt-8 pb-5 border-b border-forest/10">
                                <div className="flex items-center gap-2 text-[0.68rem] tracking-[0.3em] uppercase text-gold">
                                    <ShieldAlert size={14} /> Lye Safety · Guardian Protocol
                                </div>
                                <h2 className="font-serif text-3xl text-forest mt-3 leading-tight">
                                    Before we pour.
                                </h2>
                                <p className="text-brown text-sm mt-2 leading-relaxed">
                                    Confirm all five stations. Lye is caustic — the batch unlocks when every check is green.
                                </p>
                            </div>
                            <ul
                                className="px-7 py-6 space-y-3 max-h-[60vh] overflow-y-auto"
                                data-testid="guardian-checklist"
                            >
                                {[
                                    {
                                        key: "armor",
                                        icon: FlaskConical,
                                        title: "Armor Check",
                                        body: "Gloves, goggles, and long sleeves secured.",
                                    },
                                    {
                                        key: "atmosphere",
                                        icon: Wind,
                                        title: "Atmosphere Check",
                                        body: "Area is well-ventilated.",
                                    },
                                    {
                                        key: "emergency",
                                        icon: Flame,
                                        title: "Emergency Check",
                                        body: "Vinegar / neutralizer within reach.",
                                    },
                                    {
                                        key: "goldenRule",
                                        icon: Droplet,
                                        title: "The Golden Rule",
                                        body: "Lye is poured INTO water — never the reverse.",
                                    },
                                    {
                                        key: "perimeter",
                                        icon: HomeIcon,
                                        title: "Secure Perimeter",
                                        body: "Pets and children cleared from the area.",
                                    },
                                ].map((item, i) => {
                                    const Icon = item.icon;
                                    const active = checks[item.key];
                                    return (
                                        <li
                                            key={item.key}
                                            data-testid={`guardian-item-${item.key}`}
                                        >
                                            <label
                                                className={`flex items-start gap-4 p-4 rounded-sm border cursor-pointer transition-all duration-300 ${
                                                    active
                                                        ? "border-gold bg-gold/10"
                                                        : "border-forest/15 hover:border-forest/40 bg-cream-warm/60"
                                                }`}
                                            >
                                                <div
                                                    className={`relative shrink-0 w-9 h-9 rounded-sm flex items-center justify-center border transition-colors ${
                                                        active
                                                            ? "bg-gold border-gold text-forest"
                                                            : "bg-cream border-forest/30 text-brown"
                                                    }`}
                                                >
                                                    <Icon size={16} />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-baseline gap-2">
                                                        <span className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                                            0{i + 1}
                                                        </span>
                                                        <h3 className="font-serif text-lg text-forest">
                                                            {item.title}
                                                        </h3>
                                                    </div>
                                                    <p className="text-sm text-forest/80 mt-1 leading-relaxed">
                                                        {item.body}
                                                    </p>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={active}
                                                    onChange={(e) =>
                                                        setChecks((s) => ({
                                                            ...s,
                                                            [item.key]:
                                                                e.target.checked,
                                                        }))
                                                    }
                                                    className="mt-2 h-4 w-4 accent-[color:var(--ig-gold)]"
                                                    data-testid={`guardian-check-${item.key}`}
                                                />
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                            <div className="px-7 pb-7 pt-2 border-t border-forest/10 flex items-center justify-between gap-3 bg-cream-warm/40">
                                <p
                                    className="text-[0.65rem] tracking-[0.3em] uppercase text-brown"
                                    data-testid="guardian-progress"
                                >
                                    {
                                        Object.values(checks).filter(Boolean)
                                            .length
                                    }{" "}
                                    / 5 complete
                                </p>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setGuardianOpen(false)}
                                        className="btn-pill btn-ghost"
                                        data-testid="guardian-cancel-btn"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onRunBatch}
                                        disabled={!allChecked || confirming}
                                        className={`btn-pill ${allChecked ? "btn-gold" : "btn-primary"} disabled:opacity-40 disabled:cursor-not-allowed`}
                                        data-testid="guardian-confirm-btn"
                                    >
                                        {confirming
                                            ? "Pouring…"
                                            : "Confirm Batch"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* === Dry-Dock Waiver Modal === */}
            {waiverOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-forest-deep/85 backdrop-blur-sm"
                    role="dialog"
                    aria-modal="true"
                    data-testid="waiver-modal"
                >
                    <div className="relative max-w-lg w-full bg-cream rounded-sm shadow-2xl border border-forest/30 overflow-hidden">
                        <div className="grain absolute inset-0 opacity-25 pointer-events-none" />
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setWaiverOpen(false)}
                                className="absolute top-4 right-4 text-brown hover:text-forest"
                                aria-label="Close"
                                data-testid="waiver-close-btn"
                            >
                                <X size={18} />
                            </button>
                            <div className="px-7 pt-8 pb-5 border-b border-forest/10">
                                <div className="flex items-center gap-2 text-[0.68rem] tracking-[0.3em] uppercase text-forest">
                                    <Anchor size={14} /> Governance · Dry-Dock Waiver
                                </div>
                                <h2 className="font-serif text-3xl text-forest mt-3 leading-tight">
                                    Request a stay of the engine.
                                </h2>
                                <p className="text-brown text-sm mt-2 leading-relaxed">
                                    File one of the four sanctioned reasons below.
                                    Director K. will counter-sign.
                                </p>
                            </div>
                            <div className="px-7 py-6 space-y-5 max-h-[60vh] overflow-y-auto">
                                <div data-testid="waiver-reasons">
                                    <label className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                        Sanctioned Reason
                                    </label>
                                    <div className="mt-3 space-y-2">
                                        {waiverReasons.map((r) => (
                                            <label
                                                key={r.key}
                                                className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-all ${
                                                    waiverReason === r.key
                                                        ? "border-gold bg-gold/10"
                                                        : "border-forest/15 hover:border-forest/40 bg-cream-warm/50"
                                                }`}
                                                data-testid={`waiver-reason-${r.key}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="waiver-reason"
                                                    value={r.key}
                                                    checked={
                                                        waiverReason === r.key
                                                    }
                                                    onChange={() =>
                                                        setWaiverReason(r.key)
                                                    }
                                                    className="mt-1 h-4 w-4 accent-[color:var(--ig-gold)]"
                                                />
                                                <div>
                                                    <p className="font-serif text-base text-forest">
                                                        {r.label}
                                                    </p>
                                                    <p className="text-xs text-brown mt-0.5">
                                                        {r.subtitle}
                                                    </p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                        Optional Note (Coordinates)
                                    </label>
                                    <textarea
                                        value={waiverNote}
                                        onChange={(e) =>
                                            setWaiverNote(e.target.value)
                                        }
                                        rows={3}
                                        placeholder="Provide context for the Director…"
                                        className="mt-2 w-full bg-cream-warm/40 border border-forest/15 rounded-sm p-3 text-forest text-sm focus:border-gold outline-none"
                                        data-testid="waiver-note"
                                    />
                                </div>
                                <p
                                    className="text-[0.7rem] tracking-[0.15em] uppercase text-[#7a2a1e] bg-[#e07a5f]/15 border border-[#e07a5f]/40 rounded-sm p-3"
                                    data-testid="waiver-negative-clause"
                                >
                                    ⚠ {negativeClause}
                                </p>
                            </div>
                            <div className="px-7 pb-7 pt-2 border-t border-forest/10 flex items-center justify-between gap-3 bg-cream-warm/40">
                                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                    Routes to Director K.
                                </p>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setWaiverOpen(false)}
                                        className="btn-pill btn-ghost"
                                    >
                                        Stand Down
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onSubmitWaiver}
                                        disabled={
                                            !waiverReason || waiverSubmitting
                                        }
                                        className="btn-pill btn-primary disabled:opacity-40"
                                        data-testid="waiver-submit-btn"
                                    >
                                        {waiverSubmitting
                                            ? "Filing…"
                                            : "File Waiver"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* === Ad Boost Modal === */}
            {boostOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-forest-deep/90 backdrop-blur-sm"
                    role="dialog"
                    aria-modal="true"
                    data-testid="ad-boost-modal"
                >
                    <div className="relative max-w-xl w-full bg-cream rounded-sm shadow-2xl border border-gold/50 overflow-hidden">
                        <div
                            className="absolute inset-0 opacity-20 pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(ellipse at top right, var(--ig-gold), transparent 60%)",
                            }}
                        />
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setBoostOpen(false)}
                                className="absolute top-4 right-4 text-brown hover:text-forest"
                                aria-label="Close"
                                data-testid="ad-boost-close-btn"
                            >
                                <X size={18} />
                            </button>
                            <div className="px-7 pt-8 pb-5 border-b border-forest/10">
                                <div className="flex items-center gap-2 text-[0.68rem] tracking-[0.3em] uppercase text-gold">
                                    <Flame size={14} /> Growth Strike · Capital
                                    Injection
                                </div>
                                <h2 className="font-serif text-3xl text-forest mt-3 leading-tight">
                                    Apply for an{" "}
                                    <span className="italic">Advertisement Boost.</span>
                                </h2>
                                <p className="text-brown text-sm mt-2 leading-relaxed">
                                    Fuel for the strike. Routed to Director K. for
                                    audit.
                                </p>
                            </div>
                            <div className="px-7 py-6 space-y-5 max-h-[65vh] overflow-y-auto">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                            Boost Amount (USD)
                                        </label>
                                        <div className="mt-2 flex items-baseline gap-2 border-b border-forest/30 focus-within:border-gold pb-1">
                                            <span className="font-serif text-2xl text-brown">
                                                $
                                            </span>
                                            <input
                                                type="number"
                                                min={50}
                                                max={50000}
                                                step={50}
                                                value={boostAmount}
                                                onChange={(e) =>
                                                    setBoostAmount(e.target.value)
                                                }
                                                placeholder="0"
                                                className="flex-1 bg-transparent outline-none font-serif text-2xl text-forest"
                                                data-testid="ad-boost-amount"
                                            />
                                        </div>
                                        <p className="text-[0.6rem] text-brown mt-1">
                                            Min $50 · Max $50,000
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                            Target Platform
                                        </label>
                                        <div className="mt-2 grid grid-cols-3 gap-2">
                                            {boostMeta.platforms?.map((p) => (
                                                <button
                                                    key={p}
                                                    type="button"
                                                    onClick={() =>
                                                        setBoostPlatform(p)
                                                    }
                                                    className={`text-[0.6rem] tracking-[0.25em] uppercase py-2 rounded-full border transition-colors ${
                                                        boostPlatform === p
                                                            ? "bg-forest text-cream border-forest"
                                                            : "bg-cream-warm/60 text-forest border-forest/20 hover:border-forest"
                                                    }`}
                                                    data-testid={`ad-boost-platform-${p}`}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                        Strike Reason · Why do you need the fuel?
                                    </label>
                                    <textarea
                                        value={boostReason}
                                        onChange={(e) =>
                                            setBoostReason(e.target.value)
                                        }
                                        rows={4}
                                        placeholder="Holiday 2026 launch — paid TikTok push for the Glow Set ASMR cuts…"
                                        className="mt-2 w-full bg-cream-warm/40 border border-forest/15 rounded-sm p-3 text-forest text-sm focus:border-gold outline-none"
                                        data-testid="ad-boost-reason"
                                    />
                                </div>
                                <label
                                    className={`flex items-start gap-3 p-4 rounded-sm border cursor-pointer transition-all ${
                                        boostAck
                                            ? "border-gold bg-gold/10"
                                            : "border-[#e07a5f]/50 bg-[#e07a5f]/5"
                                    }`}
                                    data-testid="ad-boost-ack-label"
                                >
                                    <input
                                        type="checkbox"
                                        checked={boostAck}
                                        onChange={(e) =>
                                            setBoostAck(e.target.checked)
                                        }
                                        className="mt-1 h-4 w-4 accent-[color:var(--ig-gold)]"
                                        data-testid="ad-boost-ack"
                                    />
                                    <span className="text-sm text-forest leading-relaxed">
                                        <span className="font-semibold tracking-wide uppercase text-[0.65rem] text-gold mr-1">
                                            Capital Injection Clause
                                        </span>
                                        {boostMeta.disclaimer}
                                    </span>
                                </label>
                            </div>
                            <div className="px-7 pb-7 pt-2 border-t border-forest/10 flex items-center justify-between gap-3 bg-cream-warm/40">
                                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                    Routes Intel Brief to Director K.
                                </p>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setBoostOpen(false)}
                                        className="btn-pill btn-ghost"
                                    >
                                        Cancel Strike
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onSubmitBoost}
                                        disabled={
                                            boostSubmitting ||
                                            !boostAck ||
                                            !boostAmount ||
                                            !boostReason
                                        }
                                        className="btn-pill btn-gold disabled:opacity-40"
                                        data-testid="ad-boost-submit-btn"
                                    >
                                        {boostSubmitting
                                            ? "Filing…"
                                            : "File Intel Brief"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* === Commander's Log slide-out === */}
            {logOpen && (
                <div
                    className="fixed inset-0 z-[55] flex justify-end"
                    role="dialog"
                    aria-modal="true"
                    data-testid="commanders-log-drawer"
                >
                    <div
                        className="absolute inset-0 bg-forest-deep/85 backdrop-blur-sm"
                        onClick={() => setLogOpen(false)}
                    />
                    <aside className="relative w-full sm:max-w-xl bg-cream-warm border-l border-gold/40 overflow-y-auto">
                        <div className="px-6 pt-7 pb-5 flex items-start justify-between gap-3 border-b border-forest/10 bg-cream">
                            <div>
                                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                    Sidebar · Director's Desk
                                </p>
                                <h3 className="font-serif text-2xl text-forest mt-1">
                                    Commander's Log
                                </h3>
                            </div>
                            <button
                                onClick={() => setLogOpen(false)}
                                className="text-brown hover:text-forest"
                                aria-label="Close"
                                data-testid="commanders-log-close-btn"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-6">
                            <CommandersLog />
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}
